import { useState, useMemo, useEffect } from "react";
import type { ArticleBlock, ArticleCategory, DurationValue, EditorBlock } from "../types/article";

export interface ArticleEditorState {
  category: ArticleCategory | "";
  duration: DurationValue | "";
  coverFile: File | null;
  coverPreview: string | null;
  heading: string;
  bodyText: string;
  additionalBlocks: EditorBlock[];
  buttonPanelTarget: string | null;
  saving: boolean;
  isValid: boolean;
}

export interface ArticleEditorActions {
  setCategory: (v: ArticleCategory | "") => void;
  setDuration: (v: DurationValue | "") => void;
  setHeading: (v: string) => void;
  setBodyText: (v: string) => void;
  handleCoverSelect: (file: File) => void;
  addBlock: (type: "heading" | "text" | "button") => void;
  updateBlock: (id: string, content: string, link?: string) => void;
  deleteBlock: (id: string) => void;
  setButtonPanelTarget: (id: string | null) => void;
  setSaving: (v: boolean) => void;
  buildBodyBlocks: () => ArticleBlock[];
}

interface UseArticleEditorOptions {
  requireCoverFile?: boolean;
}

export function useArticleEditor(options: UseArticleEditorOptions = {}) {
  const { requireCoverFile = true } = options;

  const [category, setCategory] = useState<ArticleCategory | "">("");
  const [duration, setDuration] = useState<DurationValue | "">("");
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [heading, setHeading] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [additionalBlocks, setAdditionalBlocks] = useState<EditorBlock[]>([]);
  const [buttonPanelTarget, setButtonPanelTarget] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    return () => {
      if (coverPreview?.startsWith("blob:")) URL.revokeObjectURL(coverPreview);
    };
  }, [coverPreview]);

  function handleCoverSelect(file: File) {
    if (coverPreview?.startsWith("blob:")) URL.revokeObjectURL(coverPreview);
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  }

  const coverValid = requireCoverFile ? coverFile !== null : coverPreview !== null;

  const isValid = useMemo(
    () =>
      category !== "" &&
      duration !== "" &&
      coverValid &&
      heading.trim().length > 0 &&
      bodyText.trim().length > 0,
    [category, duration, coverValid, heading, bodyText],
  );

  function addBlock(type: "heading" | "text" | "button") {
    const id = `block-${Date.now()}`;
    setAdditionalBlocks((prev) => [...prev, { id, type, content: "", link: "" }]);
    if (type === "button") setButtonPanelTarget(id);
  }

  function updateBlock(id: string, content: string, link?: string) {
    setAdditionalBlocks((prev) =>
      prev.map((b) => (b.id === id ? { ...b, content, ...(link !== undefined ? { link } : {}) } : b)),
    );
  }

  function deleteBlock(id: string) {
    setAdditionalBlocks((prev) => prev.filter((b) => b.id !== id));
  }

  function buildBodyBlocks(): ArticleBlock[] {
    return [
      { type: "text", content: bodyText },
      ...additionalBlocks
        .filter((b) => b.content.trim())
        .map((b) => ({
          type: b.type,
          content: b.content,
          ...(b.link ? { link: b.link } : {}),
        })),
    ];
  }

  const state: ArticleEditorState = {
    category,
    duration,
    coverFile,
    coverPreview,
    heading,
    bodyText,
    additionalBlocks,
    buttonPanelTarget,
    saving,
    isValid,
  };

  const actions: ArticleEditorActions = {
    setCategory,
    setDuration,
    setHeading,
    setBodyText,
    handleCoverSelect,
    addBlock,
    updateBlock,
    deleteBlock,
    setButtonPanelTarget,
    setSaving,
    buildBodyBlocks,
  };

  return {
    state,
    actions,
    setCoverPreview,
    setAdditionalBlocks,
  };
}
